import { isAnyOf, Middleware } from "@reduxjs/toolkit";
import pDebounce from "p-debounce";
import { Animal, Customer, Metadata, State } from "../state/types";
import {
  upsertDraft,
  upsertDraftFailure,
  upsertDraftSuccess,
  updateBillingInterval,
  updateInsuranceById,
  updateBillingDay,
  removeAnimalById,
  addLiabilityInsuranceOnAnimal,
  removeLiabilityInsuranceOnAnimal,
  removeInsuranceById,
  removeAllLiabilityAddons,
} from "../slices/animals";
import { selectAllValidAnimals } from "../selectors/animals";
import client from "../../../../../apollo-client";
import { UPSERT_DRAFT } from "../queries/upsertDraft";
import {
  UpsertDraft,
  UpsertDraftVariables,
} from "../../../../../__generated__/UpsertDraft";
import { goToNextStep } from "../slices/navigation";
import { getInputFromState } from "../queries/input";

const isUpdateInsuranceAction = isAnyOf(
  updateInsuranceById,
  updateBillingInterval,
  updateBillingDay,
  removeAnimalById,
  removeInsuranceById,
  addLiabilityInsuranceOnAnimal,
  removeLiabilityInsuranceOnAnimal,
  removeAllLiabilityAddons
);

export const upsertDraftMiddleware: Middleware<{}, State> =
  (storeApi) => (next) => async (action) => {
    const result = next(action);

    if (isUpdateInsuranceAction(action)) {
      if (
        isAnyOf(updateInsuranceById)(action) &&
        action.meta?.refetch === false
      ) {
        return result;
      }
      storeApi.dispatch(upsertDraft(undefined));
    }

    if (isAnyOf(upsertDraft)(action)) {
      const state = storeApi.getState();

      const animals = selectAllValidAnimals(state);

      try {
        const qps = await debouncedFetchDrafts({
          draftId: state.metadata.draftId,
          customer: state.customer,
          metadata: state.metadata,
          animals,
        });

        if (qps.errors && (qps.errors ?? []).length > 0) {
          throw qps.errors[0];
        }

        if (!qps.data?.upsertDraft?._id) {
          throw new Error("Missing draft ID");
        }

        storeApi.dispatch(
          upsertDraftSuccess({
            clientId: qps.data.upsertDraft.clientId,
            draftId: qps.data?.upsertDraft?._id,
            drafts: qps.data?.upsertDraft?.policyPreviews ?? [],
            clientName: qps.data.upsertDraft.client?.name ?? null,
          })
        );

        if (action.meta?.navigateToNextOnSuccess) {
          storeApi.dispatch(goToNextStep({ replace: false }));
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          storeApi.dispatch(upsertDraftFailure(error));
        }
        console.log({ error });
      }
    }

    return result;
  };

export const fetchDrafts = async ({
  draftId,
  customer,
  metadata,
  animals,
}: {
  draftId: string | null;
  customer: Customer;
  metadata: Metadata;
  animals: Animal[];
}) => {
  return client.mutate<UpsertDraft, UpsertDraftVariables>({
    mutation: UPSERT_DRAFT,
    variables: {
      input: getInputFromState({ draftId, customer, metadata, animals }),
    },
  });
};

const debouncedFetchDrafts = pDebounce(fetchDrafts, 200);
